import { Component, Input, Output, EventEmitter, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DeckAdminService } from '../../deck-admin.service';

/**
 * Full-screen preview of one slide's HTML document.
 *
 * The Python service exposed slides as static /api/assets/<file> URLs; the Node
 * backend serves the HTML through the admin API instead (`includeHtml=1`), so the
 * modal fetches the document and renders it via a Blob URL — which also keeps the
 * "Open in new tab" button working.
 */
@Component({
  selector: 'app-slide-preview-modal',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div
      class="fixed inset-0 z-[9000] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      (click)="onBackdropClick($event)"
      id="slide_preview_modal_backdrop"
    >
      <div
        class="relative bg-white dark:bg-surface-1 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style="width: 92vw; max-width: 1200px; height: 85vh;"
        (click)="$event.stopPropagation()"
        id="slide_preview_modal_content"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700/60 shrink-0">
          <div class="flex items-center gap-3">
            <div class="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500"></div>
            <div>
              <h3 class="text-sm font-semibold text-slate-800 dark:text-white leading-tight">
                {{ slideTitle }}
              </h3>
              <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-mono">
                {{ fileName }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <a
              *ngIf="iframeSrc"
              [href]="iframeSrc"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700/60 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              title="Open in new tab"
              id="slide_preview_open_tab_btn"
            >
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
              Open
            </a>
            <button
              (click)="close.emit()"
              class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors"
              title="Close preview"
              id="slide_preview_close_btn"
            >
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        <!-- iframe body -->
        <div class="flex-1 relative bg-slate-50 dark:bg-surface-0">
          <div *ngIf="loading" class="absolute inset-0 flex items-center justify-center z-10">
            <div class="flex flex-col items-center gap-3">
              <div class="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
              <span class="text-xs text-slate-400">Loading slide…</span>
            </div>
          </div>
          <p
            *ngIf="errorMsg"
            class="absolute inset-0 flex items-center justify-center text-sm text-red-500 px-8 text-center"
            id="slide_preview_error"
          >
            {{ errorMsg }}
          </p>
          <iframe
            *ngIf="iframeSrc"
            [src]="iframeSrc"
            class="w-full h-full border-0"
            [class.opacity-0]="loading"
            [class.opacity-100]="!loading"
            (load)="onIframeLoad()"
            sandbox="allow-scripts allow-same-origin"
            id="slide_preview_iframe"
          ></iframe>
        </div>
      </div>
    </div>
  `,
})
export class SlidePreviewModalComponent implements OnInit, OnDestroy {
  /** Category owning the slide (the API addresses slides as group + page key). */
  @Input({ required: true }) groupKey!: string;
  /** The slide's page_key inside its category. */
  @Input({ required: true }) pageKey!: string;
  /** Stored file name — display only. */
  @Input() fileName = '';
  @Input() slideTitle = '';
  @Output() close = new EventEmitter<void>();

  private sanitizer = inject(DomSanitizer);
  private deckService = inject(DeckAdminService);
  private objectUrl: string | null = null;

  iframeSrc: SafeResourceUrl | null = null;
  loading = true;
  errorMsg = '';

  ngOnInit(): void {
    this.deckService.getPage(this.groupKey, this.pageKey, true).subscribe({
      next: (res) => {
        const html = res?.slide?.html ?? '';
        if (!html) {
          this.loading = false;
          this.errorMsg = 'This slide has no stored HTML document.';
          return;
        }
        this.objectUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
        this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
      },
      error: (err) => {
        this.loading = false;
        this.errorMsg = err?.message ?? 'Failed to load the slide.';
      },
    });
  }

  ngOnDestroy(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
  }

  onIframeLoad(): void {
    this.loading = false;
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).id === 'slide_preview_modal_backdrop') {
      this.close.emit();
    }
  }
}
